import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

/**
 * Payment status page — polls GET /payments/status/{session_id} every 2s
 * until Stripe confirms the payment (via webhook or fallback). Then shows
 * the credits granted and a CTA to the app.
 */
export default function PaymentSuccess() {
  useSeo({ title: "Pagamento completato", description: "Il tuo pacchetto crediti è stato attivato con successo.", path: "/payment/success", noindex: true });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setError("session_id mancante"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        setAttempts((a) => a + 1);
        if (data.payment_status !== "paid" && attempts < 20) {
          timer = setTimeout(poll, 2000);
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Errore di verifica");
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  const paid = status?.payment_status === "paid";
  return (
    <div className="min-h-screen bg-[#FAF9F6] grid place-items-center p-6" data-testid="payment-success-page">
      <div className="bg-white rounded-2xl shadow-xl border border-[#E8E4D9] p-10 max-w-lg w-full text-center">
        {error ? (
          <>
            <XCircle className="w-14 h-14 text-red-600 mx-auto" />
            <h1 className="mt-4 font-display font-extrabold text-3xl">Errore</h1>
            <p className="text-sm text-[#4A4A4A] mt-2">{error}</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h1 className="mt-4 font-display font-extrabold text-3xl tracking-tight">Pagamento completato</h1>
            <p className="text-[#4A4A4A] mt-2">
              Abbiamo aggiunto <strong className="text-[#0A0A0A]">{status?.credits}</strong> crediti al tuo account.
              Le fatture in coda vengono elaborate automaticamente.
            </p>
            <Link to="/app" data-testid="payment-success-cta" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#0A0A0A] text-white font-semibold hover:bg-[#1D4ED8]">
              Torna alla dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-14 h-14 text-blue-600 mx-auto animate-spin" />
            <h1 className="mt-4 font-display font-bold text-2xl">Verifica pagamento…</h1>
            <p className="text-sm text-[#4A4A4A] mt-2">Attendi qualche secondo, Stripe sta confermando.</p>
          </>
        )}
      </div>
    </div>
  );
}
